/** 从 theme.primary_color / background_color / icon 派生整套 UI 令牌 */

import { resolveAgentIcon, type AgentIconKind } from './icons';

export const DEFAULT_PRIMARY_COLOR = '#00E5FF';
export const DEFAULT_BACKGROUND_COLOR = '#000000';
export const DEFAULT_AGENT_ICON = 'default';

export type ThemeVariant = 'default' | 'embed-lite';

export interface AgentThemeTokens {
  id: string;
  primary: string;
  /** 供 rgba(var(--primary-rgb), a) 使用 */
  primaryRgb: string;
  primaryTextOn: string;
  bg: string;
  /** 供 rgba(var(--bg-rgb), a) 使用，如 "0, 0, 0" */
  bgRgb: string;
  surface: string;
  surfaceHeader: string;
  text: string;
  muted: string;
  userBubble: string;
  botBubble: string;
  reasoningBg: string;
  borderAccent: string;
  radius: string;
  fabIcon: string;
  botAvatar: string;
  /** theme.icon 解析结果 */
  iconId: string;
  iconKind: AgentIconKind;
  /** kind=image 时的头像 / FAB 图（data URI 或 URL） */
  iconSrc: string | null;
  fontFamily: string;
  glow: string;
  shadowOffset: string;
  fabShadow: string;
  panelGlow: string;
  panelBorder: string;
  /** 浅色背景（白底等） */
  light: boolean;
}

interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function normalizeHex(color: string | null | undefined): string | null {
  if (!color) return null;
  const raw = color.trim();
  const m3 = /^#?([0-9a-fA-F]{3})$/.exec(raw);
  if (m3) {
    const [a, b, c] = m3[1];
    return `#${a}${a}${b}${b}${c}${c}`.toUpperCase();
  }
  const m6 = /^#?([0-9a-fA-F]{6})$/.exec(raw);
  if (m6) return `#${m6[1]}`.toUpperCase();
  return null;
}

function hexToRgb(hex: string): Rgb | null {
  const norm = normalizeHex(hex);
  if (!norm) return null;
  const n = parseInt(norm.slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbaFromHex(hex: string, alpha: number): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return `rgba(0,229,255,${alpha})`;
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${alpha})`;
}

function relativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;
  return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}

function textOnPrimary(hex: string): string {
  return relativeLuminance(hex) > 0.58 ? '#000000' : '#FFFFFF';
}

function isLightBackground(hex: string): boolean {
  return relativeLuminance(hex) > 0.6;
}

export function buildAgentTheme(
  primaryColor?: string | null,
  variant: ThemeVariant = 'default',
  backgroundColor?: string | null,
  iconId?: string | null,
): AgentThemeTokens {
  const primary = normalizeHex(primaryColor) || DEFAULT_PRIMARY_COLOR;
  const bg = normalizeHex(backgroundColor) || DEFAULT_BACKGROUND_COLOR;
  const bgParts = hexToRgb(bg) || { r: 0, g: 0, b: 0 };
  const primaryParts = hexToRgb(primary) || { r: 0, g: 229, b: 255 };
  const lightBg = isLightBackground(bg);
  const primaryDark = relativeLuminance(primary) < 0.28;
  const lite = variant === 'embed-lite';
  const icon = resolveAgentIcon(iconId || DEFAULT_AGENT_ICON, lightBg, primary);

  // 浅底 + 深色主题色：次要文案用中性灰，避免整页过硬的纯黑字/光晕
  const muted = lightBg ? (primaryDark ? '#6B7280' : primary) : primary;
  const softShadow = lightBg
    ? '0 1px 2px rgba(0,0,0,0.06), 0 4px 12px rgba(0,0,0,0.04)'
    : `0 0 24px ${rgbaFromHex(primary, 0.15)}, inset 0 0 32px ${rgbaFromHex(primary, 0.04)}`;

  const base: AgentThemeTokens = {
    id: 'app-theme',
    primary,
    primaryRgb: `${primaryParts.r}, ${primaryParts.g}, ${primaryParts.b}`,
    primaryTextOn: textOnPrimary(primary),
    bg,
    bgRgb: `${bgParts.r}, ${bgParts.g}, ${bgParts.b}`,
    surface: lightBg ? '#F4F4F5' : '#0A0A0A',
    surfaceHeader: lightBg
      ? primaryDark
        ? 'rgba(0, 0, 0, 0.04)'
        : rgbaFromHex(primary, 0.1)
      : rgbaFromHex(primary, 0.08),
    text: lightBg ? '#111827' : '#FFFFFF',
    muted,
    userBubble: primary,
    botBubble: lightBg ? '#FFFFFF' : '#0A0A0A',
    reasoningBg: lightBg ? '#F3F4F6' : '#050505',
    borderAccent: primary,
    radius: '12px',
    fabIcon: icon.glyph,
    botAvatar: icon.glyph,
    iconId: icon.id,
    iconKind: icon.kind,
    iconSrc: icon.src,
    fontFamily: "'Atkinson Hyperlegible', system-ui, sans-serif",
    glow: lightBg
      ? softShadow
      : `0 0 40px ${rgbaFromHex(primary, 0.2)}, inset 0 0 60px ${rgbaFromHex(primary, 0.03)}`,
    shadowOffset: 'none',
    fabShadow: lightBg
      ? `0 2px 10px rgba(0,0,0,0.18), 0 0 0 2px ${rgbaFromHex(primary, 0.15)}`
      : `0 0 24px ${rgbaFromHex(primary, 0.6)}, 0 0 0 3px #ffffff`,
    panelGlow: lite
      ? lightBg
        ? '0 1px 3px rgba(0,0,0,0.08)'
        : `0 0 12px ${rgbaFromHex(primary, 0.12)}`
      : softShadow,
    panelBorder: '3px',
    light: lightBg,
  };
  if (lite && !lightBg) {
    return {
      ...base,
      glow: 'none',
      shadowOffset: 'none',
    };
  }
  if (lite && lightBg) {
    return {
      ...base,
      glow: 'none',
      shadowOffset: 'none',
    };
  }
  return base;
}

export function applyAgentThemeToRoot(
  root: HTMLElement,
  primaryColor?: string | null,
  variant: ThemeVariant = 'default',
  backgroundColor?: string | null,
  showFrameBorder = false,
  iconId?: string | null,
): AgentThemeTokens {
  const t = buildAgentTheme(primaryColor, variant, backgroundColor, iconId);
  const s = root.style;
  s.setProperty('--primary', t.primary);
  s.setProperty('--primary-rgb', t.primaryRgb);
  s.setProperty('--primary-text-on', t.primaryTextOn);
  s.setProperty('--bg', t.bg);
  s.setProperty('--bg-rgb', t.bgRgb);
  s.setProperty('--surface', t.surface);
  s.setProperty('--surface-header', t.surfaceHeader);
  s.setProperty('--text', t.text);
  s.setProperty('--muted', t.muted);
  s.setProperty('--user-bubble', t.userBubble);
  s.setProperty('--bot-bubble', t.botBubble);
  s.setProperty('--reasoning-bg', t.reasoningBg);
  s.setProperty('--border-accent', t.borderAccent);
  s.setProperty('--radius', t.radius);
  s.setProperty('--glow', t.glow);
  s.setProperty('--panel-glow', t.panelGlow);
  s.setProperty('--shadow-offset', t.shadowOffset);
  s.setProperty('--font', t.fontFamily);
  s.setProperty('--border-width', '2px');
  s.setProperty('--frame-border-width', showFrameBorder ? t.panelBorder : '0');
  s.setProperty('--header-border-width', '3px');
  s.setProperty('--footer-border-width', '3px');
  root.classList.add('theme-cyan-beam');
  root.classList.toggle('theme-cyan-beam-lite', variant === 'embed-lite');
  root.classList.toggle('theme-light', t.light);
  root.classList.toggle('frame-border-on', showFrameBorder);
  return t;
}
